/**
 * The mark: an open box with two leaves sprouting and a single fruit inside —
 * the brand's box+leaf logo, drawn inline as SVG so it scales crisply and is
 * theme-aware (leaf green on light backgrounds, reversed to milk-white on the
 * deep-green footer, per the brand style guide).
 */
import { useTheme } from '../context/ThemeContext.jsx';

export default function Logo({ className = '', showWordmark = true, tone = 'auto' }) {
  const { isDark } = useTheme();
  // 'light'/'dark' force the palette (for fixed-tone surfaces like the header or
  // footer); the default 'auto' follows the theme so a standalone logo on a
  // page background reverses to milk in dark mode instead of vanishing.
  const light = tone === 'light' || (tone !== 'dark' && isDark);
  // Reversed (on dark) = all milk-white with a mint hint; primary (on light) =
  // brand-scale green (teal under the Mint theme, via the leaf tokens) with a
  // small persimmon fruit accent.
  const box = light ? '#F4FBF2' : 'rgb(var(--c-leaf-800))';
  const leafA = light ? '#93E0AC' : 'rgb(var(--c-leaf-500))';
  const leafB = light ? '#F4FBF2' : 'rgb(var(--c-leaf-800))';
  const fruit = light ? '#F4FBF2' : '#F26430';

  const wordInk = light ? '#F4FBF2' : '#16302A';
  const wordGreen = light ? '#93E0AC' : 'rgb(var(--c-leaf-800))';

  return (
    <span className={`inline-flex min-w-0 items-center gap-2.5 ${className}`}>
      <svg viewBox="0 0 40 40" className="h-8 w-8 shrink-0" aria-hidden="true" fill="none">
        {/* leaves */}
        <path d="M20 18 C20 11 24.5 7.5 30 7.5 C30 13 25.5 17.5 20 18 Z" fill={leafA} />
        <path d="M20 18 C20 12.5 16.5 9.5 12 9.5 C12 14 15 17.8 20 18 Z" fill={leafB} />
        <path d="M20 18.5 L20 15" stroke={box} strokeWidth="2" strokeLinecap="round" />
        {/* open box */}
        <g fill="none" stroke={box} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round">
          <path d="M8.5 18.5 L31.5 18.5 L29 33 L11 33 Z" />
          <path d="M8.5 18.5 L4.5 13.5 L13.5 15.2 L20 18.5" />
          <path d="M31.5 18.5 L35.5 13.5 L26.5 15.2 L20 18.5" />
        </g>
        {/* fruit accent */}
        <circle cx="20" cy="25" r="2.5" fill={fruit} />
      </svg>
      {/* The wordmark truncates rather than being clipped mid-letter when the
          bar it sits in is tight — a phone set to large text, say. */}
      {showWordmark && (
        <span
          className="min-w-0 truncate font-display text-lg font-semibold leading-none tracking-tight"
          style={{ color: wordInk }}
        >
          Healthy<span style={{ color: wordGreen }}>Fruits</span>Box
        </span>
      )}
    </span>
  );
}
